import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  DollarSign, 
  FileText, 
  Filter, 
  RefreshCw, 
  TrendingUp, 
  Users, 
  Package,
  BarChart3,
  Download,
  Upload,
  Eye,
  Edit,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getChargebacks, 
  getChargebackStats, 
  processChargebackBatch,
  approveChargeback,
  rejectChargeback,
  exportChargebacks
} from '../../config/api';

const ChargebackProcessingDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [selectedCustomer, setSelectedCustomer] = useState('ALL');
  const [selectedDateRange, setSelectedDateRange] = useState('7d');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedChargebacks, setSelectedChargebacks] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  // Fetch chargeback statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['chargeback-stats', selectedDateRange],
    queryFn: () => getChargebackStats({ dateRange: selectedDateRange }),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch chargebacks
  const { data: chargebacksData, isLoading, error, refetch } = useQuery({
    queryKey: ['chargebacks', selectedStatus, selectedCustomer, selectedDateRange],
    queryFn: () => getChargebacks({
      status: selectedStatus,
      customerId: selectedCustomer,
      dateRange: selectedDateRange,
      page: 1,
      limit: 50
    }),
    refetchInterval: 30000
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: approveChargeback,
    onSuccess: () => {
      toast.success('Chargeback approved successfully');
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      queryClient.invalidateQueries({ queryKey: ['chargeback-stats'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve chargeback');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: rejectChargeback,
    onSuccess: () => {
      toast.success('Chargeback rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      queryClient.invalidateQueries({ queryKey: ['chargeback-stats'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject chargeback');
    }
  });

  const bulkProcessMutation = useMutation({
    mutationFn: processChargebackBatch,
    onSuccess: () => {
      toast.success('Bulk processing completed');
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      queryClient.invalidateQueries({ queryKey: ['chargeback-stats'] });
      setSelectedChargebacks([]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Bulk processing failed');
    }
  });

  const exportMutation = useMutation({
    mutationFn: exportChargebacks,
    onSuccess: (data) => {
      // Download the exported file
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chargebacks-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Export failed');
    }
  });

  // Handlers
  const handleBulkAction = () => {
    if (selectedChargebacks.length === 0) {
      toast.error('Please select chargebacks to process');
      return;
    }

    if (bulkAction === 'approve') {
      bulkProcessMutation.mutate({
        chargebackIds: selectedChargebacks,
        action: 'approve'
      });
    } else if (bulkAction === 'reject') {
      bulkProcessMutation.mutate({
        chargebackIds: selectedChargebacks,
        action: 'reject'
      });
    }
  };

  const handleSelectAll = () => {
    if (selectedChargebacks.length === chargebacksData?.chargebacks?.length) {
      setSelectedChargebacks([]);
    } else {
      setSelectedChargebacks(chargebacksData?.chargebacks?.map(cb => cb.id) || []);
    }
  };

  const handleSelectChargeback = (chargebackId) => {
    setSelectedChargebacks(prev => 
      prev.includes(chargebackId) 
        ? prev.filter(id => id !== chargebackId)
        : [...prev, chargebackId]
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'REJECTED': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'PENDING': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'PROCESSING': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'NEEDS_REVIEW': return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'PROCESSING': return 'bg-blue-100 text-blue-800';
      case 'NEEDS_REVIEW': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading || statsLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chargeback Processing Dashboard</h1>
          <p className="text-gray-600">Monitor and process chargeback claims in real-time</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => refetch()}
            title="Refresh chargebacks"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            title="Export chargebacks"
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Claims</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalClaims || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.approvedClaims || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.pendingClaims || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Needs Review</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.needsReviewClaims || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                ${stats?.totalAmount?.toLocaleString() || '0'}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved Amount</p>
              <p className="text-2xl font-bold text-green-600">
                ${stats?.approvedAmount?.toLocaleString() || '0'}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-2xl font-bold text-yellow-600">
                ${stats?.pendingAmount?.toLocaleString() || '0'}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-wrap items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="NEEDS_REVIEW">Needs Review</option>
                <option value="PROCESSING">Processing</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Date Range:</label>
              <select
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>
          </div>

          {selectedChargebacks.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {selectedChargebacks.length} selected
              </span>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="">Bulk Action</option>
                <option value="approve">Approve Selected</option>
                <option value="reject">Reject Selected</option>
              </select>
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction || bulkProcessMutation.isPending}
                title="Apply bulk action"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {bulkProcessMutation.isPending ? 'Processing...' : 'Apply'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chargebacks Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Recent Chargebacks</h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedChargebacks.length === chargebacksData?.chargebacks?.length && chargebacksData?.chargebacks?.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-600">Select All</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Select
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chargebacksData?.chargebacks?.map((chargeback) => (
                <tr key={chargeback.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedChargebacks.includes(chargeback.id)}
                      onChange={() => handleSelectChargeback(chargeback.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {chargeback.claimNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {chargeback.customer?.name || 'Unknown Customer'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${chargeback.totalAmount?.toLocaleString() || '0'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(chargeback.status)}`}>
                      {getStatusIcon(chargeback.status)}
                      <span className="ml-1">{chargeback.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(chargeback.claimDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => window.open(`/chargebacks/${chargeback.id}`, '_blank')}
                        title="View details"
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => window.open(`/chargebacks/${chargeback.id}/edit`, '_blank')}
                        title="Edit"
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {chargebacksData?.chargebacks?.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No chargebacks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No chargebacks match your current filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargebackProcessingDashboard;
